'use client';

import { useTranslations } from 'next-intl';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductLineImageToVideo } from '@/app/components/icons/ProductLineImageToVideo';
import { ProductLineReferenceToImage } from '@/app/components/icons/ProductLineReferenceToImage';
import { ProductLineReferenceToVideo } from '@/app/components/icons/ProductLineReferenceToVideo';
import { ProductLineText2Image } from '@/app/components/icons/ProductLineText2Image';
import { ProductLineTextToVideo } from '@/app/components/icons/ProductLineTextToVideo';
import { showTaskSubmitSuccessToast } from '@/app/components/ui/task-submit-success-toast';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION } from '@/app/constants';
import {
  getReferenceDeleteConfirmationCountKey,
  resolveReferenceVideoDuration,
  useForm,
  useImg2VideoForm,
  useImg2VideoModelVersionSwitch,
  useIsScreen,
  useMentionablePrompts,
  useModelVersionText,
  useProcessRecreateData,
  useReferenceDeleteConfirmation,
  useReferenceTempVideosUpload,
  useReferenceVideoDurations,
  useResizeByResolution,
  useRunTempImageUploadBatch,
  useRunTempReferenceImageUploadBatch,
  useUpgradeDialogs,
} from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  AudioUploadFile,
  ElementModality,
  ElementType,
  ImageUploadFile,
  MentionItem,
  ModelVersion,
  RecaptionPrompt,
  ReferencePrompt,
  TaskSettings,
  TextPromptWithInputItem,
  UploadFile,
  VideoUploadFile,
  ViduV1Element,
} from '@/app/types';
import {
  CREATE_FORM_INPUT_ERROR_KEY_MAP,
  convertComponents2Assets,
  convertProTextPromptToSimple,
  generateFromPage,
  getCreateFormInputValidationError,
  getDefaultModelVersionForReferenceMedia,
  getImg2VideoMaxImageCount,
  getMaxImageAndElementCount,
  getModelCapabilityKey,
  getReferenceElementModalities,
  getReferenceImageAndElementMaxCount,
  getReferenceMediaDropArgs,
  getReferenceVideoElementMaxCount,
  getReferenceVideoMaxCount,
  isReferenceVideoPrompt,
  isReferenceVideoSupported,
  mapTaskTypeToFormTabType,
  reportEvent,
  syncImageReferencePrompts,
  validateReferenceVideoDurationCombination,
} from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { useHomeGenerationFormActions } from '../../HomeGenerationFormActionsContext';
import type { MediaChip } from '../HomeGenerationFormMedia';
import {
  applyHomeImg2VideoSnapshot,
  buildHomeModeMediaPatch,
  buildReferencePromptPatch,
  buildSettingsCarryPatch,
  getHomeGenerationMethod,
  getHomeGenerationMethods,
  getHomeGenerationModeOptions,
  getHomeGenerationType,
  getHomeModeFromGeneration,
  getImageAndTextReferenceCount,
  getPlainPromptForHomeMode,
  type getSubmitTaskType,
  getTotalReferenceImageCount,
  getVisibleHomeMediaInputs,
  type HomeGenerationMethod,
  type HomeGenerationMode,
  type HomeGenerationType,
  isHomeReferenceMode,
  normalizeHomeGenerationMode,
  toFormTabType,
} from '../utils';
import { useHomeGenerationFormFloating } from './use-home-generation-form-floating';
import { useHomeGenerationFormSubmit } from './use-home-generation-form-submit';
import { useHomeImg2VideoRatioCheck } from './use-home-img2video-ratio-check';

const getHomePromptPlaceholderKey = (mode: HomeGenerationMode, modelVersion: ModelVersion) => {
  switch (mode) {
    case 'img2video':
      return 'create.img2VideoForm.input.placeholder';
    case 'character2video':
      return isReferenceVideoSupported(modelVersion)
        ? 'create.char2VideoForm.input.placeholder.q2Pro'
        : 'create.char2VideoForm.input.placeholder.noGuide';
    case 'reference2image':
      return 'create.char2VideoForm.input.placeholder.noGuide';
    case 'text2video':
    case 'text2image':
      return 'create.text2VideoForm.input.placeholder';
  }
};

export interface HomeGenerationFormController {
  dialogs: {
    elementsSelectorOpen: boolean;
    elementsSelectorAllowedModalities?: ElementModality[];
    elementsSelectorMaxReferenceCount: number;
    elementsSelectorRemainingSelections: number;
    mobileMediaOptionsOpen: boolean;
    mobileModelOpen: boolean;
    mobileSettingsOpen: boolean;
    setElementsSelectorOpen: (open: boolean) => void;
    setMobileMediaOptionsOpen: (open: boolean) => void;
    setMobileModelOpen: (open: boolean) => void;
    setMobileSettingsOpen: (open: boolean) => void;
  };
  floating: {
    collapsedPromptLabel: string;
    desktopExpandExiting: boolean;
    desktopExpandRendered: boolean;
    desktopFloatingExiting: boolean;
    desktopFloatingLayerRef: RefObject<HTMLDivElement | null>;
    desktopFloatingRendered: boolean;
    desktopFormExpanded: boolean;
    desktopFormFloating: boolean;
    desktopFormPinnedHeight: number | null;
    handleExpandFloatingForm: () => void;
    mobileFormExpanded: boolean;
    setDesktopFormExpanded: (expanded: boolean) => void;
    setMobileFormExpanded: (expanded: boolean) => void;
    submittedHomeTasks: ReturnType<typeof useHomeGenerationFormSubmit>['submittedHomeTasks'];
  };
  form: {
    currentAllowedModalities?: ElementModality[];
    currentPlainPrompt: string;
    generationMethodLabelMap: Record<HomeGenerationMethod, string>;
    generationModeOptions: Array<{
      value: HomeGenerationMode;
      label: string;
      type: HomeGenerationType;
    }>;
    generationTypeOptions: Array<{
      value: HomeGenerationType;
      label: string;
    }>;
    handleBeforeAddMention: (mentionItem: MentionItem) => boolean;
    handleBeforeDeleteMention: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
    handleCheckSettings: () => void;
    handleClearAll: () => void;
    handlePromptChange: (value: string) => void;
    imageAndTextReferenceCount: number;
    isH5: boolean;
    isProcessing: boolean;
    isSubmitDisabled: boolean;
    maxImageAndElementCount: number;
    mode: HomeGenerationMode;
    modelVersion: string;
    modelVersionLabel: string;
    promptPlaceholder: string;
    referencePrompts: ReferencePrompt[];
    referenceTextPrompt: TextPromptWithInputItem;
    settings?: TaskSettings;
    shouldUpgradeForModel: boolean;
    submitTaskType: ReturnType<typeof getSubmitTaskType>;
    updateReferenceTextPrompt: (value: TextPromptWithInputItem) => void;
    updateSettings: (newValues: Partial<TaskSettings>) => void;
    validationErrorMessage?: string;
  };
  generation: {
    handleGenerationMethodChange: (value: string) => void;
    handleGenerationModeChange: (value: string) => void;
    handleGenerationTypeChange: (value: string) => void;
    // 视频主体需要先异步探测时长再决定能否入列，因此返回 Promise。
    handleElementsSelect: (elements: ElementType[]) => Promise<void>;
    method: HomeGenerationMethod;
    type: HomeGenerationType;
  };
  media: {
    availableMobileMediaInputs: ReturnType<typeof getVisibleHomeMediaInputs>;
    canUploadMixedReferenceMedia: boolean;
    canUploadReferenceVideo: boolean;
    isQ2ProSeparatedMediaAreas: boolean;
    videoMediaChips: MediaChip[];
    imageMediaChips: MediaChip[];
    subjectMediaChips: MediaChip[];
    dropZone: {
      acceptImage: boolean;
      acceptVideo: boolean;
      disabled: boolean;
      maxCount: number;
      maxImageCount: number;
      maxVideoCount: number;
      minVideoDuration?: number;
      maxVideoDuration?: number;
      maxVideoSize?: number;
    };
    handleMobileMediaOptionImageChange: (file: ImageUploadFile) => void;
    handleMobileMediaOptionMediaChange: (file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => void;
    handleMobileMediaOptionVideoChange: (file: VideoUploadFile) => void;
    handleOpenMobileMediaOptions: () => void;
    handleSelectSubject: (allowedModalities?: ElementModality[]) => void;
    handleUploadImage: (file: ImageUploadFile) => void;
    handleUploadReferenceMedia: (file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => void;
    handleVideoUploadClick: () => boolean;
    imageUploadMaxImageSize: number;
    mediaChips: MediaChip[];
    mixedReferenceMediaActionLabel: string;
    mixedReferenceMediaMaxCount: number;
    referenceVideoConfig: ReturnType<typeof getReferenceMediaDropArgs>;
    remainingImageUploadCount: number;
    shouldUseMixedReferenceMediaUpload: boolean;
    showImageInput: boolean;
    showMobileImageInput: boolean;
    showMobileMediaRow: boolean;
    showMobileSubjectInput: boolean;
    showSubjectInput: boolean;
    visibleMediaInputs: ReturnType<typeof getVisibleHomeMediaInputs>;
  };
  imageCrop: ReturnType<typeof useHomeImg2VideoRatioCheck>;
  multiFrame: {
    draft: string;
    isMultiFrameMode: boolean;
    openIndex: number | null;
    setDraft: (value: string) => void;
    setOpenIndex: (index: number | null) => void;
    handleTransitionUpgrade: (key: string) => void;
    transitions: ReturnType<typeof useImg2VideoForm>['multiFrameTransitions'];
    updateTransitionDuration: (index: number, duration: number) => void;
    updateTransitionPrompt: (index: number, prompt: string) => void;
    validImageCount: number;
  };
  refs: {
    desktopFormAnchorRef: RefObject<HTMLElement | null>;
    multiFramePromptRef: RefObject<HTMLTextAreaElement | null>;
    plainPromptRef: RefObject<HTMLTextAreaElement | null>;
  };
  submit: {
    handleBeforeSubmit: () => boolean | Promise<boolean>;
    handleSubmitSuccess: (
      task: ReturnType<typeof useHomeGenerationFormSubmit>['submittedHomeTasks'][number],
    ) => 'suppressDefaultToast';
    maxImageCount: number;
  };
}

/**
 * 主体（element）类型的参考没有直接的预览字段，要从它的第一个 component/asset 上推。
 *
 * modality 优先取 element 自己声明的；声明缺失或为 unspecified 时按第一个 component 的
 * 形状兜底（文本 component → text，asset 带 video 字段 → video，其余 → image）。
 * 非 element 类型返回全空，由调用方用 item.prompt 上的字段。
 */
const resolveReferenceElementPreview = (
  reference: ReferencePrompt,
): {
  modality?: ElementModality;
  preview?: string;
  videoPreview?: string;
  videoCover?: string;
} => {
  if (reference.type !== 'element') {
    return {};
  }

  const firstComponent = reference.prompt?.components?.[0];
  const firstAsset = convertComponents2Assets(reference.prompt?.components)[0] || undefined;
  const declaredModality = reference.prompt?.modality;
  const modality: ElementModality =
    declaredModality && declaredModality !== 'unspecified'
      ? declaredModality
      : firstComponent?.content_type === 'text'
        ? 'text'
        : firstAsset
          ? 'video' in firstAsset
            ? 'video'
            : 'image'
          : 'text';

  if (modality === 'video') {
    const cover =
      firstAsset && 'coverImagePreview' in firstAsset
        ? firstAsset.coverImagePreview || firstAsset.coverImage
        : undefined;

    return {
      modality,
      preview: cover,
      videoPreview:
        firstAsset && 'videoPreview' in firstAsset ? firstAsset.videoPreview || firstAsset.video : undefined,
      videoCover: cover,
    };
  }

  if (modality === 'image') {
    return {
      modality,
      preview:
        firstAsset && 'selectedImagePreview' in firstAsset
          ? firstAsset.selectedImagePreview || firstAsset.sourceImagePreview
          : undefined,
    };
  }

  return { modality };
};

const resolveElementModality = (element: ViduV1Element): Exclude<ElementModality, 'unspecified'> => {
  if (element.modality && element.modality !== 'unspecified') {
    return element.modality;
  }

  const firstContentType = element.components?.[0]?.content_type;
  if (firstContentType === 'video' || firstContentType === 'text') {
    return firstContentType;
  }

  return 'image';
};

export const useHomeGenerationFormController = (): HomeGenerationFormController => {
  const t = useTranslations();
  // 关闭专业模式时把 recaptionPrompt 折叠成普通提示词要用到分区文案，与创作页一致。
  const transProMode = useTranslations('create.proMode');
  const locale = useLocale();
  const store = useStoreApi();
  const { isSmScreen: isH5 } = useIsScreen();
  const homeGenerationFormActions = useHomeGenerationFormActions();
  const { getModelVersionText } = useModelVersionText();
  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const [multiFrameTransitionOpenIndex, setMultiFrameTransitionOpenIndex] = useState<number | null>(null);
  const [multiFrameTransitionDraft, setMultiFrameTransitionDraft] = useState('');
  const [elementsSelectorAllowedModalities, setElementsSelectorAllowedModalities] = useState<
    ElementModality[] | undefined
  >();
  const floatingState = useHomeGenerationFormFloating({ isH5 });
  const { processRecreateData } = useProcessRecreateData();

  const {
    formParams,
    homeGenerationMode,
    modelVersion,
    img2videoModelVersion,
    hiddenReferencePrompts,
    setFormParams,
    setHiddenReferencePrompts,
    updateHomeGenerationMode,
    updateFormParams,
    updateModelVersion,
  } = useStore((state) => ({
    formParams: state[`${toFormTabType(normalizeHomeGenerationMode(state.homeGenerationMode))}FormParams`],
    homeGenerationMode: state.homeGenerationMode,
    modelVersion:
      state[`${toFormTabType(normalizeHomeGenerationMode(state.homeGenerationMode))}FormParams`].modelVersion,
    img2videoModelVersion: state.img2videoFormParams.modelVersion,
    hiddenReferencePrompts: state.hiddenReferencePrompts,
    setFormParams: state.setFormParams,
    setHiddenReferencePrompts: state.setHiddenReferencePrompts,
    updateHomeGenerationMode: state.updateHomeGenerationMode,
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
  }));
  const mode = normalizeHomeGenerationMode(homeGenerationMode);

  // 传入 onOpenPlanDialog 才能让 useFormValidation 的 handleUpgradeClick 生效，
  // 从而复用独立创作页 `{模型}_{表单类型}_{原因}` 的 from_page 埋点格式。
  const currentForm = useForm(mode, handleOpenPlanDialog);

  // The home form has no professional-mode UI. If a form state was left with
  // that flag by a standalone creation page, returning to home closes it
  // immediately so submission uses the same normal-mode semantics as the
  // visible home controls.
  //
  // 关闭前要跟独立创作页的 forceCloseProMode 一样，把 recaptionPrompt 折叠回普通提示词
  // 再清空，否则用户在创作页写的专业模式内容会在回到首页时被静默丢弃。
  useEffect(() => {
    if (!formParams.isProMode) {
      return;
    }

    const { recaptionPrompt } = formParams;
    const hasProContent = !!recaptionPrompt?.subject && !!recaptionPrompt?.environment;
    if (!hasProContent) {
      setFormParams(mode, { isProMode: false, recaptionPrompt: {} as RecaptionPrompt });
      return;
    }

    // 开启专业模式时basic prompt 已经被折叠进 recaptionPrompt，所以它才是最新内容，
    // 这里直接覆盖回普通提示词（与 forceCloseProMode 的行为保持一致），不做保留合并。
    const convertedText = convertProTextPromptToSimple(recaptionPrompt, {
      t: transProMode,
      outputFormat: 'text',
    }) as string;

    if (isHomeReferenceMode(mode)) {
      setFormParams(mode, {
        ...buildReferencePromptPatch({
          text: convertedText,
          referencePrompts: formParams.referencePrompts || [],
          mode,
        }),
        isProMode: false,
        recaptionPrompt: {} as RecaptionPrompt,
      });
      return;
    }

    setFormParams(mode, {
      textPrompt: convertedText,
      isProMode: false,
      recaptionPrompt: {} as RecaptionPrompt,
    });
  }, [formParams, mode, setFormParams, transProMode]);

  const {
    settings,
    updateTextPrompt,
    updateSettings,
    formValidationErrorMessage,
    handleCheckSettings,
    handleBeforeProModeSubmit,
    handleUpgradeClick,
    shouldUpgradeForModel: shouldUpgradeForCurrentFormModel,
  } = currentForm;
  const generationTypeOptions = useMemo(
    () =>
      (['video', 'image'] as const).map((item) => ({
        value: item,
        label:
          item === 'video'
            ? t('home.generationForm.generationTypes.video')
            : t('home.generationForm.generationTypes.image'),
      })),
    [t],
  );
  // 方式文案随类型变化（reference/text 在视频与图片下是不同 tab 文案），所以按 type 取而不是按当前 mode。
  const getGenerationMethodLabel = useCallback(
    (type: HomeGenerationType, method: HomeGenerationMethod) => {
      if (method === 'image') {
        return t('create.tabImg2Video');
      }

      if (method === 'reference') {
        return type === 'video' ? t('create.tabCharacter2Video') : t('create.tabReference2Image');
      }

      return type === 'video' ? t('create.tabText2Video') : t('create.tabText2Image');
    },
    [t],
  );
  const generationMethodLabelMap = useMemo((): Record<HomeGenerationMethod, string> => {
    const type = getHomeGenerationType(mode);

    return {
      reference: getGenerationMethodLabel(type, 'reference'),
      image: getGenerationMethodLabel(type, 'image'),
      text: getGenerationMethodLabel(type, 'text'),
    };
  }, [getGenerationMethodLabel, mode]);
  // 桌面端把「类型」「方式」合成一个下拉，选项是两者的全部有效组合。
  // label 只放方式文案，类型由 select 里的分组标题承载。
  const generationModeOptions = useMemo(
    () =>
      getHomeGenerationModeOptions().map(({ method, mode: optionMode, type }) => {
        // 根据模式返回对应的 icon 组件（存引用，不实例化 JSX）
        let IconComponent: React.ComponentType<{ className?: string }> | undefined;
        if (optionMode === 'character2video') {
          // 参考生视频
          IconComponent = ProductLineReferenceToVideo;
        } else if (optionMode === 'img2video') {
          // 图生视频
          IconComponent = ProductLineImageToVideo;
        } else if (optionMode === 'text2video') {
          // 文生视频
          IconComponent = ProductLineTextToVideo;
        } else if (optionMode === 'reference2image') {
          // 参考生图
          IconComponent = ProductLineReferenceToImage;
        } else if (optionMode === 'text2image') {
          // 文生图
          IconComponent = ProductLineText2Image;
        }

        return {
          label: getGenerationMethodLabel(type, method),
          type,
          value: optionMode,
          iconComponent: IconComponent,
        };
      }),
    [getGenerationMethodLabel],
  );

  const { maxImageSize: img2VideoMaxImageSize, imageProcessing: img2VideoImageProcessing } =
    useResizeByResolution('img2video');
  const { maxImageSize: character2videoMaxImageSize, imageProcessing: character2videoImageProcessing } =
    useResizeByResolution('character2video');
  const { maxImageSize: reference2imageMaxImageSize, imageProcessing: reference2imageImageProcessing } =
    useResizeByResolution('reference2image');

  const {
    imagePrompts,
    validImageCount,
    handleUploadImage: handleUploadImg2VideoImage,
    handleImageChange: handleImg2VideoImageChange,
    handleRemoveImage: handleRemoveImg2VideoImage,
    handleSwapImages: handleSwapImg2VideoImages,
    clearImages: clearImg2VideoImages,
    setImages: setImg2VideoImages,
    setCurrentTransitionIndex,
    multiFrameTransitions,
    updateTransitionDuration: updateImg2VideoTransitionDuration,
    updateTransitionPrompt: updateImg2VideoTransitionPrompt,
  } = useImg2VideoForm({
    maxCount: getImg2VideoMaxImageCount(img2videoModelVersion),
    allowTempMode: true,
  });

  const img2VideoModelVersionSwitch = useImg2VideoModelVersionSwitch({
    imagePrompts,
    setImages: setImg2VideoImages,
    setCurrentTransitionIndex,
  });

  const imageCrop = useHomeImg2VideoRatioCheck({
    enabled: mode === 'img2video',
    imagePrompts,
    maxImageSize: img2VideoMaxImageSize,
    userInfo: store.getState().userInfo,
    setImages: setImg2VideoImages,
    handleImageChange: handleImg2VideoImageChange,
    handleRemoveImage: handleRemoveImg2VideoImage,
    handleSwapImages: handleSwapImg2VideoImages,
  });

  useEffect(() => {
    if (mode !== 'img2video' || validImageCount < 3) {
      setMultiFrameTransitionOpenIndex(null);
      setMultiFrameTransitionDraft('');
      return;
    }

    const activeIndex = multiFrameTransitionOpenIndex ?? 0;
    const activeTransition = multiFrameTransitions?.[activeIndex];
    setMultiFrameTransitionDraft(activeTransition?.prompt || '');
  }, [mode, validImageCount, multiFrameTransitionOpenIndex, multiFrameTransitions]);

  const character2videoPrompts = useMentionablePrompts('character2video', {
    allowTempMode: true,
  });
  const reference2imagePrompts = useMentionablePrompts('reference2image', {
    allowTempMode: true,
  });

  useRunTempReferenceImageUploadBatch(
    character2videoPrompts.referencePrompts,
    character2videoPrompts.updateReferenceAndMentions,
  );
  useReferenceTempVideosUpload(
    character2videoPrompts.referencePrompts,
    character2videoPrompts.updateReferenceAndMentions,
  );
  useRunTempReferenceImageUploadBatch(
    reference2imagePrompts.referencePrompts,
    reference2imagePrompts.updateReferenceAndMentions,
  );
  useRunTempImageUploadBatch(imagePrompts, handleImg2VideoImageChange);

  // Q2 的 img2video 多帧（>2 帧）不支持错峰，强制回普通队列。
  // 只在当前版本确实是 Q2 时才写，否则会去改一个没生效的 settings bucket。
  useEffect(() => {
    if (validImageCount <= 2 || img2videoModelVersion !== MODEL_VERSION.Q2) {
      return;
    }

    const { settingsQ2 } = store.getState().img2videoFormParams;
    if (settingsQ2.schedule_mode === 'normal') {
      return;
    }

    updateFormParams('img2video', 'settingsQ2', {
      ...settingsQ2,
      schedule_mode: 'normal',
    });
  }, [img2videoModelVersion, store, updateFormParams, validImageCount]);

  const referenceModeDraftsRef = useRef<
    Partial<
      Record<HomeGenerationMode, { referencePrompts: ReferencePrompt[]; overflowReferencePrompts: ReferencePrompt[] }>
    >
  >({});
  const img2VideoModeSwitchSnapshotRef = useRef<ReturnType<
    typeof img2VideoModelVersionSwitch.takeImagesForModeSwitch
  > | null>(null);

  const currentPlainPrompt = getPlainPromptForHomeMode(mode, formParams);
  const currentReferencePrompts = mode === 'reference2image' ? reference2imagePrompts : character2videoPrompts;
  const {
    textPrompt: referenceTextPrompt,
    referencePrompts,
    updateTextPrompt: updateReferenceTextPrompt,
    addReferenceAndMentions,
    removeReferenceAndMentions,
    handleUploadImage: handleUploadReferenceImage,
    handleClearPrompts,
    handleBeforeAddMention,
    handleBeforeDeleteMention: handleMentionableBeforeDeleteMention,
    showMaxReferenceError: showCharacter2videoMaxReferenceError,
    resetMaxReferenceError: resetCharacter2videoMaxReferenceError,
  } = currentReferencePrompts;
  const {
    showMaxReferenceError: showReference2imageMaxReferenceError,
    resetMaxReferenceError: resetReference2imageMaxReferenceError,
  } = reference2imagePrompts;

  const { handleBeforeDeleteMention } = useReferenceDeleteConfirmation({
    confirmationCountKey: getReferenceDeleteConfirmationCountKey('character2video'),
    onConfirmDelete: (referenceIdsToDelete) => {
      referenceIdsToDelete.forEach((mentionId) => {
        removeReferenceAndMentions(mentionId);
      });
    },
    onShowConfirm: (referenceIdsToDelete, updatedContent) =>
      handleMentionableBeforeDeleteMention(referenceIdsToDelete, updatedContent),
  });

  const handleModeChange = (value: string) => {
    const nextMode = normalizeHomeGenerationMode(value);
    if (nextMode === mode) return;

    const text = getPlainPromptForHomeMode(mode, formParams);
    if (isHomeReferenceMode(mode)) {
      referenceModeDraftsRef.current[mode] = {
        referencePrompts: [...(formParams.referencePrompts || [])],
        overflowReferencePrompts: referenceModeDraftsRef.current[mode]?.overflowReferencePrompts || [],
      };
    }

    const modeSwitchSnapshot = mode === 'img2video' ? img2VideoModelVersionSwitch.takeImagesForModeSwitch() : undefined;
    if (modeSwitchSnapshot) {
      img2VideoModeSwitchSnapshotRef.current = modeSwitchSnapshot;
    }

    const sourceFormParams =
      mode === 'img2video'
        ? {
            ...formParams,
            imagePrompts: modeSwitchSnapshot?.images || [],
          }
        : formParams;
    const targetFormParams = store.getState()[`${toFormTabType(nextMode)}FormParams`];
    const targetReferenceDraft = isHomeReferenceMode(nextMode) ? referenceModeDraftsRef.current[nextMode] : undefined;
    const preservedReferencePrompts = targetReferenceDraft
      ? [...targetReferenceDraft.referencePrompts, ...targetReferenceDraft.overflowReferencePrompts]
      : undefined;
    let mediaPatch = buildHomeModeMediaPatch({
      text,
      sourceMode: mode,
      targetMode: nextMode,
      sourceFormParams,
      targetFormParams,
      locale,
      preservedReferencePrompts,
    });
    if (nextMode === 'img2video') {
      const snapshot = img2VideoModeSwitchSnapshotRef.current;
      if (snapshot) {
        mediaPatch = applyHomeImg2VideoSnapshot({ sourceMode: mode, mediaPatch, snapshot });
      }
    }
    const settingsPatch = buildSettingsCarryPatch({
      sourceFormParams,
      targetFormParams: {
        ...targetFormParams,
        ...mediaPatch,
      },
      targetMode: nextMode,
      t,
    });

    if (isHomeReferenceMode(nextMode) && mediaPatch.referencePrompts) {
      const targetModelVersion = settingsPatch.modelVersion || targetFormParams.modelVersion;
      const maxImageAndElementCount = getReferenceImageAndElementMaxCount(targetModelVersion);
      const maxVideoCount = getReferenceVideoMaxCount(targetModelVersion, mediaPatch.referencePrompts);
      let imageAndElementCount = 0;
      let videoCount = 0;
      const visibleReferencePrompts: ReferencePrompt[] = [];
      const overflowReferencePrompts: ReferencePrompt[] = [];

      for (const reference of mediaPatch.referencePrompts) {
        const isVideo = isReferenceVideoPrompt(reference);
        const canKeep = isVideo ? videoCount < maxVideoCount : imageAndElementCount < maxImageAndElementCount;
        if (canKeep) {
          visibleReferencePrompts.push(reference);
          if (isVideo) videoCount += 1;
          else imageAndElementCount += 1;
        } else {
          overflowReferencePrompts.push(reference);
        }
      }
      if (overflowReferencePrompts.length > 0 || referenceModeDraftsRef.current[nextMode]) {
        referenceModeDraftsRef.current[nextMode] = {
          referencePrompts: visibleReferencePrompts,
          overflowReferencePrompts,
        };
      }
      if (visibleReferencePrompts.length !== mediaPatch.referencePrompts.length) {
        mediaPatch = {
          ...mediaPatch,
          ...buildReferencePromptPatch({ text, referencePrompts: visibleReferencePrompts, mode: nextMode }),
        };
      }
    }

    if (nextMode === 'img2video' && isHomeReferenceMode(mode)) {
      imageCrop.markModeSwitch();
    }

    setFormParams(nextMode, {
      ...mediaPatch,
      ...settingsPatch,
      // Professional mode is not available on the home form. Entering or
      // switching between home modes must never retain a hidden standalone
      // form professional-mode flag.
      isProMode: false,
    });
    updateHomeGenerationMode(nextMode);
  };

  const handleRecreateToHome = (params: import('@/app/types').RecreateToHomeParams) => {
    const tab = mapTaskTypeToFormTabType(params.taskType);
    if (!tab) return false;
    processRecreateData({
      tab,
      settings: params.settings,
      input: params.input,
      creationId: params.creationId,
      inspirationId: params.inspirationId,
      source: params.source,
      taskType: params.taskType,
    });
    updateHomeGenerationMode(tab);
    floatingState.handleExpandFloatingForm();
    if (isH5) floatingState.setMobileFormExpanded(true);
    return true;
  };

  const recreateToHomeRef = useRef(handleRecreateToHome);
  recreateToHomeRef.current = handleRecreateToHome;

  useEffect(() => {
    return homeGenerationFormActions?.registerRecreateHandler((params) => recreateToHomeRef.current(params));
  }, [homeGenerationFormActions]);

  const handleGenerationTypeChange = (value: string) => {
    const nextType: HomeGenerationType = value === 'image' ? 'image' : 'video';
    const currentMethod = getHomeGenerationMethod(mode);
    const availableMethods = getHomeGenerationMethods(nextType);
    const nextMethod = availableMethods.includes(currentMethod) ? currentMethod : availableMethods[0];
    const nextMode = getHomeModeFromGeneration(nextType, nextMethod);
    if (nextMode) {
      handleModeChange(nextMode);
    }
  };

  const handleGenerationMethodChange = (value: string) => {
    const nextMethod = value as HomeGenerationMethod;
    const nextMode = getHomeModeFromGeneration(getHomeGenerationType(mode), nextMethod);
    if (nextMode) {
      handleModeChange(nextMode);
    }
  };

  const handlePromptChange = (value: string) => {
    if (isHomeReferenceMode(mode)) {
      updateReferenceTextPrompt(
        buildReferencePromptPatch({ text: value, referencePrompts, mode }).textPromptWithReference,
      );
      return;
    }

    updateTextPrompt(value);
  };

  const handleElementsSelect = async (elements: ElementType[]) => {
    const existingIds = new Set(referencePrompts.map((item) => item.id));
    const newElements = elements.filter((element) => !!element.id && !existingIds.has(element.id));

    // 视频主体要先校验时长组合再入列：不能假设主体一定 <= 5s（官方预置主体由后端配置，
    // 不经过前端上传校验），否则「2 个 5s 内」的规则会被绕过。
    const videoElements = newElements.filter((element) => element.modality === 'video');
    if (videoElements.length > 0) {
      const durations = await Promise.all(
        videoElements.map((element) =>
          resolveReferenceVideoDuration(
            element.components?.find((component) => component.content_type === 'video')?.content,
          ),
        ),
      );
      const validation = validateReferenceVideoDurationCombination(
        character2videoModelVersion,
        referencePrompts,
        durations,
      );
      if (!validation.ok) {
        toast({ type: 'warning', message: t('create.createForm.upload.videoDurationLimit') });
        return;
      }
    }

    newElements.forEach((element) => {
      const newReference: ReferencePrompt = {
        id: element.id as string,
        type: 'element',
        name: element.name || '',
        status: 'done',
        prompt: element,
      };
      addReferenceAndMentions(newReference);
    });
    floatingState.setElementsSelectorOpen(false);
    reportEvent('my_element_click', { click_position: 'home_generation_form' });
  };

  const switchToReferenceMode = (nextMode: Extract<HomeGenerationMode, 'character2video' | 'reference2image'>) => {
    if (!isHomeReferenceMode(mode)) {
      handleModeChange(nextMode);
    }
  };

  const handleSelectSubject = (allowedModalities?: ElementModality[]) => {
    switchToReferenceMode(mode === 'text2image' ? 'reference2image' : 'character2video');
    setElementsSelectorAllowedModalities(allowedModalities);
    floatingState.setElementsSelectorOpen(true);
  };

  const handleUsePostReference = async (element: ViduV1Element, traceId?: string) => {
    const promptId = element.id || traceId || element.name || '';
    if (!promptId) {
      return;
    }

    const targetMode: HomeGenerationMode = 'character2video';
    if (mode !== targetMode) {
      handleModeChange(targetMode);
    }

    const modality = resolveElementModality(element);
    if (modality === 'video') {
      const targetModelVersion =
        getDefaultModelVersionForReferenceMedia('character2video', 'video') ?? MODEL_VERSION.Q2_PRO;
      if (store.getState().character2videoFormParams.modelVersion !== targetModelVersion) {
        updateModelVersion('character2video', targetModelVersion);
      }
    }

    const targetFormParams = store.getState().character2videoFormParams;
    const targetModelVersion = targetFormParams.modelVersion;
    const existingReferencePrompts = targetFormParams.referencePrompts || [];
    if (existingReferencePrompts.some((item) => item.id === promptId)) {
      floatingState.handleExpandFloatingForm();
      return;
    }

    const allowedModalities = getReferenceElementModalities(targetModelVersion);
    if (allowedModalities && !allowedModalities.includes(modality)) {
      return;
    }

    if (modality === 'video') {
      const videoDuration = await resolveReferenceVideoDuration(
        element.components?.find((component) => component.content_type === 'video')?.content,
      );
      const validation = validateReferenceVideoDurationCombination(targetModelVersion, existingReferencePrompts, [
        videoDuration,
      ]);
      if (!validation.ok) {
        toast({ type: 'warning', message: t('create.createForm.upload.videoDurationLimit') });
        return;
      }
    } else {
      const maxReferenceCount = getReferenceImageAndElementMaxCount(targetModelVersion);
      if (getImageAndTextReferenceCount(existingReferencePrompts) >= maxReferenceCount) {
        toast({
          type: 'warning',
          message: t('create.char2VideoForm.dialog.maxReferenceError.content', { count: maxReferenceCount }),
        });
        return;
      }
    }

    const nextReferencePrompt: ReferencePrompt = {
      id: promptId,
      name: element.name || t('home.generationForm.media.subject'),
      prompt: element,
      status: 'done',
      type: 'element',
    };
    character2videoPrompts.addReferenceAndMentions(nextReferencePrompt);

    floatingState.handleExpandFloatingForm();
  };

  // handleUsePostReference 每次渲染都是新闭包（依赖 mode / formParams / floatingState 都在变），
  // 包 useCallback 也稳不住。这里存进 ref，只注册一个稳定的转发函数，避免每渲染注销重注册。
  const usePostReferenceRef = useRef(handleUsePostReference);
  usePostReferenceRef.current = handleUsePostReference;

  useEffect(() => {
    return homeGenerationFormActions?.registerSubjectUseHandler((element, traceId) =>
      usePostReferenceRef.current(element, traceId),
    );
  }, [homeGenerationFormActions]);

  const handleUploadImage = (file: ImageUploadFile) => {
    const shouldUseReference2Image = mode === 'text2image' || mode === 'reference2image';
    if (shouldUseReference2Image) {
      if (file.status === 'uploading') {
        handleModeChange('reference2image');
      }
      reference2imagePrompts.handleUploadImage(file);
      return;
    }

    if (isHomeReferenceMode(mode)) {
      handleUploadReferenceImage(file);
      return;
    }

    if (mode !== 'img2video' && file.status === 'uploading') {
      handleModeChange('img2video');
    }
    if (file.status === 'uploading') {
      imageCrop.markUploadBatchStart();
    }
    handleUploadImg2VideoImage(file);
  };

  const handleVideoUploadClick = () => {
    if (mode !== 'character2video') {
      handleModeChange('character2video');
    }
    const currentVersion = store.getState().character2videoFormParams.modelVersion;
    const videoReferenceModelVersion =
      getDefaultModelVersionForReferenceMedia('character2video', 'video') ?? MODEL_VERSION.Q2_PRO;
    if (currentVersion !== videoReferenceModelVersion) {
      const updates = syncImageReferencePrompts({
        prevVersion: currentVersion,
        nextVersion: videoReferenceModelVersion,
        formParams: store.getState().character2videoFormParams,
        locale,
        hiddenReferencePrompts,
      });
      if (updates.referencePrompts) {
        updateFormParams('character2video', 'referencePrompts', updates.referencePrompts);
      }
      if (updates.textPromptWithReference) {
        updateFormParams('character2video', 'textPromptWithReference', updates.textPromptWithReference);
      }
      if (updates.imagePrompts) {
        updateFormParams('character2video', 'imagePrompts', updates.imagePrompts);
      }
      if (updates.textPrompt !== undefined) {
        updateFormParams('character2video', 'textPrompt', updates.textPrompt);
      }
      if (updates.hiddenItems !== undefined) {
        setHiddenReferencePrompts(getModelCapabilityKey(currentVersion), updates.hiddenItems);
      }
      updateModelVersion('character2video', videoReferenceModelVersion);
    }

    return true;
  };

  const { character2videoModelVersion, reference2imageModelVersion } = useStore((state) => ({
    character2videoModelVersion: state.character2videoFormParams.modelVersion,
    reference2imageModelVersion: state.reference2imageFormParams.modelVersion,
  }));
  const currentReferenceModelVersion =
    mode === 'reference2image' ? reference2imageModelVersion : character2videoModelVersion;
  const maxImageAndElementCount = getMaxImageAndElementCount(currentReferenceModelVersion);
  useEffect(() => {
    const activeShowMaxReferenceError =
      mode === 'reference2image' ? showReference2imageMaxReferenceError : showCharacter2videoMaxReferenceError;
    if (!isHomeReferenceMode(mode) || !activeShowMaxReferenceError) {
      return;
    }

    toast({
      type: 'warning',
      message: t('create.char2VideoForm.dialog.maxReferenceError.content', {
        count: maxImageAndElementCount,
      }),
    });

    if (mode === 'reference2image') {
      resetReference2imageMaxReferenceError();
    } else {
      resetCharacter2videoMaxReferenceError();
    }
  }, [
    maxImageAndElementCount,
    mode,
    resetCharacter2videoMaxReferenceError,
    resetReference2imageMaxReferenceError,
    showCharacter2videoMaxReferenceError,
    showReference2imageMaxReferenceError,
    t,
  ]);
  const imageAndTextReferenceCount = getImageAndTextReferenceCount(referencePrompts);
  // 探测视频/视频主体的真实时长，探测完成后重新计算额度（1 个 5-8s 或 2 个 5s 内）。
  useReferenceVideoDurations(character2videoPrompts.referencePrompts);
  const referenceVideoConfig = getReferenceMediaDropArgs(
    character2videoModelVersion,
    character2videoPrompts.referencePrompts,
  );
  const isQ2ProSeparatedMediaAreas = mode === 'character2video' && character2videoModelVersion === MODEL_VERSION.Q2_PRO;
  const character2videoAllowedModalities = getReferenceElementModalities(character2videoModelVersion);
  const reference2imageAllowedModalities = getReferenceElementModalities(reference2imageModelVersion);
  const modelAllowedModalities = useMemo(() => {
    const allowedModalities =
      mode === 'reference2image' ? reference2imageAllowedModalities : character2videoAllowedModalities;

    return allowedModalities ? [...allowedModalities] : undefined;
  }, [character2videoAllowedModalities, mode, reference2imageAllowedModalities]);
  const shouldUploadImageAsReference =
    mode === 'character2video' || mode === 'reference2image' || mode === 'text2image';
  const imageUploadReferencePrompts =
    mode === 'reference2image' || mode === 'text2image'
      ? reference2imagePrompts.referencePrompts
      : character2videoPrompts.referencePrompts;
  const imageUploadReferenceModelVersion =
    mode === 'reference2image' || mode === 'text2image' ? reference2imageModelVersion : character2videoModelVersion;
  const remainingImageUploadCount = shouldUploadImageAsReference
    ? Math.max(
        0,
        getMaxImageAndElementCount(imageUploadReferenceModelVersion) -
          getImageAndTextReferenceCount(imageUploadReferencePrompts),
      )
    : Math.max(0, getImg2VideoMaxImageCount(img2videoModelVersion) - validImageCount);

  const validationErrorMessage = useMemo(() => {
    const inputValidationError = getCreateFormInputValidationError(toFormTabType(mode), formParams);
    if (inputValidationError) {
      return t(CREATE_FORM_INPUT_ERROR_KEY_MAP[inputValidationError]);
    }

    return formValidationErrorMessage;
  }, [mode, formParams, formValidationErrorMessage, t]);

  const mediaChips = useMemo((): MediaChip[] => {
    if (mode === 'img2video') {
      return imagePrompts
        .filter((item): item is NonNullable<typeof item> => !!item)
        .map((item, index) => ({
          id: item.id,
          label: t('home.generationForm.media.imageFrame', { index: index + 1 }),
          mobileBadge: index === 0 ? undefined : String(index + 1),
          preview: item.prompt?.selectedImagePreview || item.prompt?.sourceImagePreview,
          type: 'image' as const,
          loading: item.status === 'uploading',
          removable: true,
          onRemove: () => handleRemoveImg2VideoImage(index),
        }));
    }

    if (isHomeReferenceMode(mode)) {
      return referencePrompts.map((item) => {
        const element = resolveReferenceElementPreview(item);

        return {
          id: item.id,
          label: item.name || t('home.generationForm.media.subject'),
          preview:
            item.type === 'image'
              ? item.prompt?.selectedImagePreview || item.prompt?.sourceImagePreview
              : item.type === 'video'
                ? item.prompt?.coverImagePreview
                : element.preview,
          videoPreview: item.type === 'video' ? item.prompt?.videoPreview : element.videoPreview,
          videoCover:
            item.type === 'video' ? item.prompt?.coverImagePreview || item.prompt?.coverImage : element.videoCover,
          type:
            item.type === 'video'
              ? ('video' as const)
              : item.type === 'element'
                ? ('element' as const)
                : ('image' as const),
          elementModality: element.modality,
          isShareElement: item.type === 'element' && item.prompt?.share_meta?.status === 'approved',
          loading: item.status === 'uploading',
          removable: true,
          onRemove: () => removeReferenceAndMentions(item.id),
        };
      });
    }

    return [];
  }, [mode, imagePrompts, referencePrompts, t, handleRemoveImg2VideoImage, removeReferenceAndMentions]);

  // 只有真正会上传图片的模式才有 resize 中间态。text2video / text2image 没有图片入口，
  // 不能借用 character2video 的 processing，否则会把提交按钮卡在无提示的禁用态。
  const isProcessing =
    mode === 'img2video'
      ? img2VideoImageProcessing
      : mode === 'character2video'
        ? character2videoImageProcessing
        : mode === 'reference2image'
          ? reference2imageImageProcessing
          : false;
  const imageUploadMaxImageSize =
    mode === 'reference2image' || mode === 'text2image'
      ? reference2imageMaxImageSize
      : mode === 'character2video'
        ? character2videoMaxImageSize
        : img2VideoMaxImageSize;
  const visibleMediaInputs = getVisibleHomeMediaInputs(mode);
  const showSubjectInput = visibleMediaInputs.includes('subject');
  const showImageInput = visibleMediaInputs.includes('image');
  const referenceVideoSupported = mode === 'character2video' && isReferenceVideoSupported(character2videoModelVersion);
  const shouldUseMixedReferenceMediaUpload = mode === 'character2video' && showImageInput && referenceVideoSupported;
  const canUploadReferenceVideo = referenceVideoSupported && referenceVideoConfig.maxVideoCount > 0;
  const canDropImage = mode !== 'text2video' && remainingImageUploadCount > 0;
  const canDropVideo = shouldUseMixedReferenceMediaUpload && canUploadReferenceVideo;
  const canUploadMixedReferenceMedia = shouldUseMixedReferenceMediaUpload
    ? remainingImageUploadCount > 0 || canUploadReferenceVideo
    : false;
  const currentAllowedModalities = useMemo(() => {
    const canSelectImageOrText = imageAndTextReferenceCount < maxImageAndElementCount;

    if (mode === 'character2video' && modelAllowedModalities === undefined) {
      if (canSelectImageOrText && canUploadReferenceVideo) return undefined;
      if (canSelectImageOrText) return ['text', 'image'] as ElementModality[];
      if (canUploadReferenceVideo) return ['video'] as ElementModality[];
      return [] as ElementModality[];
    }

    return canSelectImageOrText ? modelAllowedModalities : [];
  }, [canUploadReferenceVideo, imageAndTextReferenceCount, maxImageAndElementCount, mode, modelAllowedModalities]);
  const mixedReferenceMediaMaxCount =
    remainingImageUploadCount + (canUploadReferenceVideo ? referenceVideoConfig.maxVideoCount : 0);
  const dropZoneMaxImageCount = canDropImage ? remainingImageUploadCount : 0;
  const dropZoneMaxVideoCount = canDropVideo ? referenceVideoConfig.maxVideoCount : 0;
  const mixedReferenceMediaActionLabel = shouldUseMixedReferenceMediaUpload
    ? remainingImageUploadCount > 0 && canUploadReferenceVideo
      ? `${t('home.generationForm.actions.image')} / ${t('home.generationForm.actions.video')}`
      : canUploadReferenceVideo
        ? t('home.generationForm.actions.video')
        : t('home.generationForm.actions.image')
    : t('home.generationForm.actions.image');
  const availableMobileMediaInputs = visibleMediaInputs.filter((input) => {
    if (input === 'image') {
      return shouldUseMixedReferenceMediaUpload ? canUploadMixedReferenceMedia : remainingImageUploadCount > 0;
    }

    return (
      imageAndTextReferenceCount < maxImageAndElementCount ||
      (shouldUseMixedReferenceMediaUpload && canUploadReferenceVideo)
    );
  });
  const showMobileSubjectInput = availableMobileMediaInputs.includes('subject');
  const showMobileImageInput = availableMobileMediaInputs.includes('image');
  const showMobileMediaRow = mediaChips.length > 0 || availableMobileMediaInputs.length > 0;
  const videoReferenceCount = referencePrompts.filter(
    (reference) =>
      reference.type === 'video' || (reference.type === 'element' && reference.prompt?.modality === 'video'),
  ).length;

  const handleClearAll = () => {
    if (isHomeReferenceMode(mode)) {
      handleClearPrompts();
    } else if (mode === 'img2video') {
      clearImg2VideoImages();
      updateFormParams('img2video', 'textPrompt', '');
    } else {
      updateFormParams(toFormTabType(mode), 'textPrompt', '');
    }
  };

  const handleOpenMobileMediaOptions = () => {
    if (availableMobileMediaInputs.length <= 1) {
      return;
    }
    floatingState.setMobileMediaOptionsOpen(true);
  };

  const handleMobileMediaOptionImageChange = (file: ImageUploadFile) => {
    if (file.status === 'uploading') {
      floatingState.setMobileMediaOptionsOpen(false);
    }
    handleUploadImage(file);
  };

  const handleUploadReferenceMedia = (file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => {
    if (type === 'video') {
      if (file.status === 'uploading') {
        handleVideoUploadClick();
      }
      character2videoPrompts.handleUploadVideo(file as VideoUploadFile);
      return;
    }

    if (type === 'image') {
      handleUploadImage(file as ImageUploadFile);
    }
  };

  const handleMobileMediaOptionMediaChange = (
    file: UploadFile | AudioUploadFile,
    type: 'image' | 'video' | 'audio',
  ) => {
    if (file.status === 'uploading') {
      floatingState.setMobileMediaOptionsOpen(false);
    }
    handleUploadReferenceMedia(file, type);
  };

  const handleMobileMediaOptionVideoChange = (file: VideoUploadFile) => {
    if (file.status === 'uploading') {
      floatingState.setMobileMediaOptionsOpen(false);
    }
    character2videoPrompts.handleUploadVideo(file);
  };

  const submitState = useHomeGenerationFormSubmit({
    handleBeforeProModeSubmit,
    handleCheckSettings,
    handleUpgradeClick,
    handleReferenceImageOverflowWarning: () => {
      toast({
        type: 'warning',
        message: t('create.char2VideoForm.tooManyImagesWarning.content', {
          count: maxImageAndElementCount,
        }),
      });
    },
    img2videoModelVersion,
    isProMode: formParams.isProMode,
    isProcessing,
    mode,
    shouldUpgradeForModel: shouldUpgradeForCurrentFormModel,
    shouldWarnReferenceImageOverflow:
      isHomeReferenceMode(mode) &&
      !isReferenceVideoSupported(currentReferenceModelVersion) &&
      getTotalReferenceImageCount(referencePrompts) > maxImageAndElementCount,
    validationErrorMessage,
    validImageCount,
  });
  const handleSubmitSuccess = (task: ReturnType<typeof useHomeGenerationFormSubmit>['submittedHomeTasks'][number]) => {
    const result = submitState.handleSubmitSuccess(task);

    showTaskSubmitSuccessToast(t('popup.toast.taskSubmitSuccess'));

    if (isH5) {
      floatingState.setMobileFormExpanded(false);
    }

    return result;
  };
  const promptPlaceholder = t(getHomePromptPlaceholderKey(mode, formParams.modelVersion));
  const videoElementSelectionMaxCount = getReferenceVideoElementMaxCount(character2videoModelVersion);

  return {
    dialogs: {
      elementsSelectorOpen: floatingState.elementsSelectorOpen,
      elementsSelectorAllowedModalities,
      // 只选视频主体时给到模型原始上限（2），允许一次挑两个：主体时长能在入列前探测出来，
      // handleElementsSelect 会按「1 个 5-8s 或 2 个 5s 内」校验并拒绝超额组合。
      // 上传视频不行——时长要等上传/元数据读完才知道，仍然沿用带时长判断的额度。
      elementsSelectorMaxReferenceCount:
        elementsSelectorAllowedModalities?.length === 1 && elementsSelectorAllowedModalities[0] === 'video'
          ? videoElementSelectionMaxCount
          : maxImageAndElementCount,
      elementsSelectorRemainingSelections:
        elementsSelectorAllowedModalities?.length === 1 && elementsSelectorAllowedModalities[0] === 'video'
          ? Math.max(0, videoElementSelectionMaxCount - videoReferenceCount)
          : Math.max(0, maxImageAndElementCount - imageAndTextReferenceCount),
      mobileMediaOptionsOpen: floatingState.mobileMediaOptionsOpen,
      mobileModelOpen: floatingState.mobileModelOpen,
      mobileSettingsOpen: floatingState.mobileSettingsOpen,
      setElementsSelectorOpen: (open) => {
        if (!open) {
          setElementsSelectorAllowedModalities(undefined);
        }
        floatingState.setElementsSelectorOpen(open);
      },
      setMobileMediaOptionsOpen: floatingState.setMobileMediaOptionsOpen,
      setMobileModelOpen: floatingState.setMobileModelOpen,
      setMobileSettingsOpen: floatingState.setMobileSettingsOpen,
    },
    floating: {
      collapsedPromptLabel: currentPlainPrompt.trim() || promptPlaceholder,
      desktopExpandExiting: floatingState.desktopExpandExiting,
      desktopExpandRendered: floatingState.desktopExpandRendered,
      desktopFloatingExiting: floatingState.desktopFloatingExiting,
      desktopFloatingLayerRef: floatingState.desktopFloatingLayerRef,
      desktopFloatingRendered: floatingState.desktopFloatingRendered,
      desktopFormExpanded: floatingState.desktopFormExpanded,
      desktopFormFloating: floatingState.desktopFormFloating,
      desktopFormPinnedHeight: floatingState.desktopFormPinnedHeight,
      handleExpandFloatingForm: floatingState.handleExpandFloatingForm,
      mobileFormExpanded: floatingState.mobileFormExpanded,
      setDesktopFormExpanded: floatingState.setDesktopFormExpanded,
      setMobileFormExpanded: floatingState.setMobileFormExpanded,
      submittedHomeTasks: submitState.submittedHomeTasks,
    },
    form: {
      currentAllowedModalities,
      generationMethodLabelMap,
      generationModeOptions,
      generationTypeOptions,
      currentPlainPrompt,
      handleBeforeAddMention,
      handleBeforeDeleteMention,
      handleCheckSettings,
      handleClearAll,
      handlePromptChange,
      imageAndTextReferenceCount,
      isH5,
      isProcessing,
      isSubmitDisabled: submitState.isSubmitDisabled,
      maxImageAndElementCount,
      mode,
      modelVersion,
      modelVersionLabel: getModelVersionText({ modelVersion }),
      promptPlaceholder,
      referencePrompts,
      referenceTextPrompt,
      settings,
      shouldUpgradeForModel: shouldUpgradeForCurrentFormModel,
      submitTaskType: submitState.submitTaskType,
      updateReferenceTextPrompt,
      updateSettings,
      validationErrorMessage,
    },
    generation: {
      handleElementsSelect,
      handleGenerationMethodChange,
      handleGenerationModeChange: handleModeChange,
      handleGenerationTypeChange,
      method: getHomeGenerationMethod(mode),
      type: getHomeGenerationType(mode),
    },
    imageCrop,
    media: {
      availableMobileMediaInputs,
      canUploadMixedReferenceMedia,
      canUploadReferenceVideo,
      isQ2ProSeparatedMediaAreas,
      videoMediaChips: mediaChips.filter(
        (chip) => chip.type === 'video' || (chip.type === 'element' && chip.elementModality === 'video'),
      ),
      imageMediaChips: mediaChips.filter((chip) => chip.type === 'image'),
      subjectMediaChips: mediaChips.filter((chip) => chip.type === 'element' && chip.elementModality !== 'video'),
      dropZone: {
        acceptImage: canDropImage,
        acceptVideo: canDropVideo,
        disabled: !canDropImage && !canDropVideo,
        maxCount: dropZoneMaxImageCount + dropZoneMaxVideoCount,
        maxImageCount: dropZoneMaxImageCount,
        maxVideoCount: dropZoneMaxVideoCount,
        minVideoDuration: referenceVideoConfig.minVideoDuration,
        maxVideoDuration: referenceVideoConfig.maxVideoDuration,
        maxVideoSize: referenceVideoConfig.maxVideoSize,
      },
      handleMobileMediaOptionImageChange,
      handleMobileMediaOptionMediaChange,
      handleMobileMediaOptionVideoChange,
      handleOpenMobileMediaOptions,
      handleSelectSubject,
      handleUploadImage,
      handleUploadReferenceMedia,
      handleVideoUploadClick,
      imageUploadMaxImageSize,
      mediaChips,
      mixedReferenceMediaActionLabel,
      mixedReferenceMediaMaxCount,
      referenceVideoConfig,
      remainingImageUploadCount,
      shouldUseMixedReferenceMediaUpload,
      showImageInput,
      showMobileImageInput,
      showMobileMediaRow,
      showMobileSubjectInput,
      showSubjectInput,
      visibleMediaInputs,
    },
    multiFrame: {
      draft: multiFrameTransitionDraft,
      isMultiFrameMode: mode === 'img2video' && validImageCount >= 3,
      openIndex: multiFrameTransitionOpenIndex,
      setDraft: setMultiFrameTransitionDraft,
      setOpenIndex: setMultiFrameTransitionOpenIndex,
      handleTransitionUpgrade: (key) => {
        handleOpenPlanDialog(generateFromPage({ modelVersion, type: 'img2video', key }));
      },
      transitions: multiFrameTransitions,
      updateTransitionDuration: updateImg2VideoTransitionDuration,
      updateTransitionPrompt: updateImg2VideoTransitionPrompt,
      validImageCount,
    },
    refs: {
      desktopFormAnchorRef: floatingState.desktopFormAnchorRef,
      multiFramePromptRef: floatingState.multiFramePromptRef,
      plainPromptRef: floatingState.plainPromptRef,
    },
    submit: {
      handleBeforeSubmit: submitState.handleBeforeSubmit,
      handleSubmitSuccess,
      maxImageCount: submitState.maxImageCount,
    },
  };
};
